import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { useLocale } from '../context/LocaleContext';
import { Layers, CheckCircle, Trash2, Archive, Tag } from 'lucide-react';

const BatchOperations = ({ items, itemType, onRefresh }) => {
  const { toast } = useToast();
  const { t } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [performing, setPerforming] = useState(false);

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
  };

  const handleToggleItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  const handleBatchOperation = async (operation) => {
    if (selectedItems.length === 0) {
      toast({
        title: t('work.noItemsSelected') || 'No Items Selected',
        description: t('work.pleaseSelectItems') || 'Please select items first',
        variant: 'default'
      });
      return;
    }

    setPerforming(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assistant/batch-operation`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            item_type: itemType,
            item_ids: selectedItems,
            operation: operation
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast({
          title: '✅ ' + (t('work.batchOperationComplete') || 'Batch Operation Complete'),
          description: `${operation} ${t('work.appliedToItems') || 'applied to'} ${selectedItems.length} ${t('work.items') || 'items'}`
        });
        setSelectedItems([]);
        setIsOpen(false);
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error('Batch operation error:', error);
      toast({
        title: t('common.error') || 'Operation Failed',
        description: t('work.unableToBatch') || 'Unable to complete batch operation',
        variant: 'destructive'
      });
    } finally {
      setPerforming(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-teal-300">
          <Layers className="h-4 w-4 mr-2" />
          {t('work.batchActions') || 'Batch Actions'}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-teal-600" />
            {t('work.batchOperations') || 'Batch Operations'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedItems.length === items.length ? (t('common.deselectAll') || 'Deselect All') : (t('common.selectAll') || 'Select All')}
            </Button>
            <Badge variant="secondary">
              {selectedItems.length} / {items.length} {t('work.selected') || 'selected'}
            </Badge>
          </div>

          {/* Items List */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-lg p-3">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 p-3 border rounded hover:bg-gray-50 cursor-pointer"
                onClick={() => handleToggleItem(item.id)}
              >
                <Checkbox
                  checked={selectedItems.includes(item.id)}
                  onCheckedChange={() => handleToggleItem(item.id)}
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {item.title || item.name || (t('common.untitled') || 'Untitled')}
                  </p>
                  {item.description && (
                    <p className="text-xs text-gray-600 truncate">{item.description}</p>
                  )}
                </div>
                {item.priority && (
                  <Badge variant={item.priority === 'high' ? 'destructive' : 'secondary'}>
                    {item.priority === 'high' ? 'High' : item.priority === 'medium' ? 'Medium' : item.priority === 'low' ? 'Low' : item.priority}
                  </Badge>
                )}
              </div>
            ))}
          </div>

          {/* Batch Actions */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => handleBatchOperation('complete')}
              disabled={performing || selectedItems.length === 0}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {t('work.markComplete') || 'Mark Complete'}
            </Button>
            <Button
              onClick={() => handleBatchOperation('delete')}
              disabled={performing || selectedItems.length === 0}
              variant="destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('actions.delete') || 'Delete'}
            </Button>
            <Button
              onClick={() => handleBatchOperation('archive')}
              disabled={performing || selectedItems.length === 0}
              variant="outline"
            >
              <Archive className="h-4 w-4 mr-2" />
              {t('work.archive') || 'Archive'}
            </Button>
            <Button
              onClick={() => handleBatchOperation('tag')}
              disabled={performing || selectedItems.length === 0}
              variant="outline"
            >
              <Tag className="h-4 w-4 mr-2" />
              {t('work.addTag') || 'Add Tag'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BatchOperations;